import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { enumToItems } from "@utils/strings";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Button, Headline, Input, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ModalityTypesEnum,
  PayorsEnum,
  ServicesOfferedEnum,
  VerificationStatusEnum,
  getBasecampClinicListingsRetrieveQueryKey,
  useBasecampClinicListingsCreateCreate,
  useBasecampClinicListingsUpdateCreate,
  useBasecampGoogleAutocompleteRetrieve,
  useBasecampGooglePlaceMetadataRetrieve,
} from "@api/rest/types-and-hooks";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

import { ClinicFormPreview } from "./ClinicFormPreview";

export interface ClinicFormValues {
  name: string;
  address: string;
  phone: string;
  website: string;
  email: string;
  about: string;
  services: string[];
  modalities: string[];
  payors: string[];
}

interface AddEditClinicFormProps {
  orgId: string;
  clinicId?: string;
  initialValues?: Partial<ClinicFormValues>;
  onCancel: () => void;
}

export const AddEditClinicForm: React.FC<AddEditClinicFormProps> = ({ orgId, clinicId, initialValues, onCancel }) => {
  const isEditing = !!clinicId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [addressSearchTerm, setAddressSearchTerm] = useState("");
  const [selectedPlaceId, setSelectedPlaceId] = useState("");
  const [debouncedAddressSearchTerm] = useDebounce(addressSearchTerm, 200);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isAddressSelectedRef = useRef(false);

  const { data: autocompleteData } = useBasecampGoogleAutocompleteRetrieve(
    { search_term: debouncedAddressSearchTerm },
    { query: { enabled: !!debouncedAddressSearchTerm } },
  );

  const { data: placeData } = useBasecampGooglePlaceMetadataRetrieve(
    { place_id: selectedPlaceId },
    { query: { enabled: !!selectedPlaceId } },
  );

  const addressResults = autocompleteData?.status === 200 ? autocompleteData.data : undefined;
  const placeMetadata = placeData?.status === 200 ? placeData.data : undefined;

  const { mutateAsync: createClinicListing } = useBasecampClinicListingsCreateCreate();
  const { mutateAsync: updateClinicListing } = useBasecampClinicListingsUpdateCreate();

  const {
    formState,
    onSubmit,
    register,
    setValue,
    watch,
    control,
    reset,
    RegisteredFormInput,
    RegisteredFormTextArea,
    RegisteredCheckboxList,
  } = useFormUtils<ClinicFormValues>({
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      website: "",
      email: "",
      about: "",
      services: [],
      modalities: [],
      payors: [],
    },
    mutationFn: async (values) => {
      if (isEditing) {
        const response = await updateClinicListing({
          data: {
            clinic_listing_id: clinicId!,
            name: values.name,
            description: values.about || null,
            website_url: values.website || null,
            phone_number: values.phone || null,
            contact_email: values.email || null,
            full_address: values.address || null,
            services_offered: values.services as ServicesOfferedEnum[],
            modality_types: values.modalities as ModalityTypesEnum[],
            payors: values.payors as PayorsEnum[],
          },
        });

        if (response.status !== 200) {
          notifyError("Failed to update clinic. Please try again.");
          return;
        }

        notifySuccess("Clinic updated successfully");
        queryClient.invalidateQueries({ queryKey: getBasecampClinicListingsRetrieveQueryKey() });
        navigate(`/organizations/${orgId}?tab=clinic-listings`);
        return;
      }

      const response = await createClinicListing({
        data: {
          name: values.name,
          org_id: orgId,
          description: values.about || null,
          website_url: values.website || null,
          phone_number: values.phone || null,
          contact_email: values.email || null,
          full_address: values.address || null,
          services_offered: values.services as ServicesOfferedEnum[],
          modality_types: values.modalities as ModalityTypesEnum[],
          payors: values.payors as PayorsEnum[],
          verification_status: VerificationStatusEnum.VERIFIED,
        },
      });

      if (response.status !== 201) {
        notifyError(response.data.detail || "Failed to create clinic. Please try again.");
        return;
      }

      notifySuccess("Clinic added successfully");
      queryClient.invalidateQueries({ queryKey: getBasecampClinicListingsRetrieveQueryKey() });
      navigate(`/organizations/${orgId}?tab=clinic-listings`);
    },
  });

  useEffect(() => {
    if (!initialValues) return;
    reset(initialValues as ClinicFormValues);
    if (initialValues.address) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAddressSearchTerm(initialValues.address);
      isAddressSelectedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateAddress = useCallback(
    () => isAddressSelectedRef.current || "Please select a valid address from the suggestions",
    [],
  );

  const {
    onChange: rhfAddressOnChange,
    ref: addressRef,
    name: addressName,
    onBlur: addressOnBlur,
    // eslint-disable-next-line react-hooks/refs
  } = register("address", {
    required: "Address is required",
    validate: validateAddress,
  });

  const addressValue = watch("address") ?? "";
  const addressError = formState.errors["address"]?.message?.toString();

  useEffect(() => {
    if (!placeMetadata?.formatted_address) return;
    isAddressSelectedRef.current = true;
    setValue("address", placeMetadata.formatted_address, { shouldValidate: true });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAddressSearchTerm(placeMetadata.formatted_address);
    setShowSuggestions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeMetadata]);

  const addressWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col gap-6 pt-4 break-words">
      {!isEditing && (
        <button
          type="button"
          onClick={onCancel}
          className="flex flex-row items-center gap-2 w-fit hover:opacity-90 transition-opacity"
        >
          <ArrowLeftIcon className="size-4 text-rio-600" style={{ strokeWidth: 2 }} />
          <Paragraph className="!text-rio-600" displayType="loud" size="small">
            Back to search
          </Paragraph>
        </button>
      )}

      <form onSubmit={onSubmit}>
        <div className="flex flex-row gap-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col p-8 border border-border-medium bg-bg-surface rounded-xl">
              <Headline className="pb-2">Basic Information</Headline>

              <RegisteredFormInput formKey="name" label="Clinic name" placeholder="Harborview ABA Center" />

              <div ref={addressWrapperRef} className="relative mb-4">
                <Input
                  name={addressName}
                  ref={addressRef}
                  label="Address"
                  variant="dropdown"
                  inputSize="full"
                  value={addressValue}
                  onBlur={addressOnBlur}
                  onChange={(e) => {
                    rhfAddressOnChange(e);
                    setAddressSearchTerm(e.target.value);
                    isAddressSelectedRef.current = false;
                    setShowSuggestions(true);
                  }}
                  placeholder="1915 Chopper St. Denver, CO 80202"
                  formNoValidate={showSuggestions}
                  errorMessage={!showSuggestions ? addressError : undefined}
                />
                {showSuggestions && addressResults && addressResults.results.length > 0 && (
                  <div className="absolute left-0 right-0 z-10 flex flex-col gap-1 py-2 bg-bg-surface border border-border-medium rounded-md">
                    {addressResults.results.map((result) => (
                      <div
                        key={result.place_id}
                        className="cursor-pointer hover:bg-limestone-50 px-2"
                        onMouseDown={() => {
                          if (selectedPlaceId === result.place_id && placeMetadata?.formatted_address) {
                            isAddressSelectedRef.current = true;
                            setValue("address", placeMetadata.formatted_address, { shouldValidate: true });
                            setAddressSearchTerm(placeMetadata.formatted_address);
                            setShowSuggestions(false);
                          } else {
                            setSelectedPlaceId(result.place_id);
                          }
                        }}
                      >
                        <Paragraph colorType="primary" size="small">
                          {result.address}
                        </Paragraph>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-row gap-4">
                <RegisteredFormInput
                  formKey="phone"
                  label="Phone number"
                  placeholder="(123) 456-7890"
                  required={false}
                  inputSize="sm"
                />
                <RegisteredFormInput
                  formKey="website"
                  label="Website"
                  placeholder="harborviewaba.com"
                  required={false}
                  inputSize="sm"
                />
              </div>

              <RegisteredFormInput
                formKey="email"
                label="Contact email"
                placeholder="leads@harborviewaba.com"
                required={false}
              />

              <RegisteredFormTextArea
                formKey="about"
                label="About your clinic"
                required={false}
                placeholder="We provide comprehensive ABA therapy services..."
                rows={4}
              />
            </div>

            <div className="flex flex-col p-8 border border-border-medium bg-bg-surface rounded-xl">
              <Headline>Services offered</Headline>
              <RegisteredCheckboxList
                formKey="services"
                title="Services"
                orientation="vertical"
                items={enumToItems(ServicesOfferedEnum)}
                required={false}
              />
            </div>

            <div className="flex flex-col p-8 border border-border-medium bg-bg-surface rounded-xl">
              <Headline>Service details</Headline>
              <RegisteredCheckboxList
                formKey="modalities"
                title="Modalities"
                orientation="vertical"
                items={enumToItems(ModalityTypesEnum)}
                required={false}
              />
            </div>

            <div className="flex flex-col p-8 border border-border-medium bg-bg-surface rounded-xl">
              <Headline>Insurance accepted</Headline>
              <RegisteredCheckboxList
                formKey="payors"
                title="Insurance"
                orientation="vertical"
                items={enumToItems(PayorsEnum)}
                required={false}
              />
            </div>

            <div className="flex flex-row gap-2 mt-2 justify-end">
              <Button
                type="button"
                variant="primary"
                buttonStyle="border"
                text="Cancel"
                onClick={onCancel}
                disabled={formState.isSubmitting}
              />
              <Button
                type="submit"
                variant="primary"
                buttonStyle="filled"
                text={formState.isSubmitting ? "Saving..." : isEditing ? "Save" : "Submit"}
                disabled={formState.isSubmitting}
              />
            </div>
          </div>

          <ClinicFormPreview control={control} />
        </div>
      </form>
    </div>
  );
};
